import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { trackEvent } from '@/modules/analytics';
import {
  purlSignUp,
  validatePurlToken,
  verifyPurlDob,
  type PurlSignUpPayload,
} from '@/modules/api';
import type { User } from '@/types/user';

import type { PurlState, PurlStatus, PurlSubmitResult, PurlVerifyResult } from '../types/purl';

// PURL ("Personalized URL") pre-authenticated enrollment. A member arrives via a
// personalized link carrying a token + org; the token already proves eligibility,
// so the flow skips company-search / eligibility entirely. This hook owns the
// PURL async actions + their state (mirrors useEligibilityCheck's "action owns
// loading + error state" pattern). It drives two screens via the resolver:
//   1. a dedicated DOB-verification entry screen — `verifyDob` (3-attempt/lockout);
//   2. account creation — `submitPurlAccount` (sign-up with the verified DOB).
//
// The token is stored in state on validation so it survives the URL dropping
// `?token=` on the first redirect; `verifiedDob`'s presence is what advances the
// resolver from the entry screen to account creation.

type DobParts = { dobYear: string; dobMonth: string; dobDay: string };

type UsePurlArgs = {
  /** Raw `?token=` URL param (read by the provider on entry routes). */
  tokenParam: string | undefined;
  /** Raw `?org=` URL param — passed straight to validate, as Sparta does. */
  orgParam: string | undefined;
  /**
   * Whether auth has settled to unauthenticated (the "user endpoint returned a
   * 401" check). Validation only runs when true — a user with a live session
   * never validates a token; they fall through to the normal flow.
   */
  isUnauthenticated: boolean;
  /** Core's user refresh — called after a created account to flip authStatus. */
  refreshUser: () => Promise<User>;
};

export type UsePurl = {
  purl: PurlState;
  verifyDob: (dob: DobParts) => Promise<PurlVerifyResult>;
  /** "Verify another way" — abandon the PURL flow and fall through to normal onboarding. */
  verifyAnotherWay: () => void;
  submitPurlAccount: (payload: PurlSignUpPayload) => Promise<PurlSubmitResult>;
};

export function usePurl({
  tokenParam,
  orgParam,
  isUnauthenticated,
  refreshUser,
}: UsePurlArgs): UsePurl {
  const [status, setStatus] = useState<PurlStatus>('unset');
  const [orgName, setOrgName] = useState<string | undefined>(undefined);
  // Stored on validation so it outlives the `?token=` URL param (dropped on the
  // first redirect); read by verifyDob + the account-creation payload.
  const [token, setToken] = useState<string | undefined>(undefined);
  const [verifiedDob, setVerifiedDob] = useState<string | undefined>(undefined);
  const [redirectUrl, setRedirectUrl] = useState<string | undefined>(undefined);
  // Guards the validate effect so it fires once per distinct token.
  const validatedTokenRef = useRef<string | undefined>(undefined);

  // Validate the token: on `valid` enter the PURL flow (+ stash the org name).
  // On `invalid` / `already_enrolled` the backend returns a `redirectUrl` — record
  // it (+ the status) so the redirect screen can send the member there (prod) or
  // open the sign-in sheet (dev). The redirect isn't done here because a hook
  // can't open a bottom sheet. A network/HTTP error has no URL, so it falls back
  // to normal onboarding (status 'invalid', no redirectUrl).
  const runPurlValidate = useCallback(async (tok: string, org: string) => {
    setStatus('validating');
    setToken(tok);
    try {
      const response = await validatePurlToken(tok, org);
      if (response.status === 'valid') {
        // Prefer the backend's paying-org name; fall back to the `?org=` param
        // (which is only in the URL at validate time). Stored here so screens read
        // it from state — the param is dropped from the URL on the next redirect.
        setOrgName(response.payingOrgName ?? org);
        setStatus('valid');
        trackEvent('purl-token-validated', { status: 'valid' });
        return;
      }
      // invalid | already_enrolled — both carry a redirectUrl.
      setRedirectUrl(response.redirectUrl);
      setStatus(response.status);
      trackEvent('purl-token-validated', { status: response.status });
    } catch {
      setStatus('invalid');
    }
  }, []);

  // Verify the entered DOB against the b2b record. Handles every verify-dob
  // status (mirrors Sparta's purlVerifyDobEpic):
  //   verified → store the DOB (its presence advances the resolver to account creation);
  //   mismatch → stay on the screen for an inline retry;
  //   locked / invalid / already_enrolled / configuration_error → PURL can't
  //     continue, so flip to a terminal status and let the resolver fall the
  //     member through to normal onboarding (Sparta redirects these to onboarding);
  //   network/unexpected error → 'error' so the screen offers a retry.
  const verifyDob = useCallback(
    async ({ dobYear, dobMonth, dobDay }: DobParts): Promise<PurlVerifyResult> => {
      if (!token) return { status: 'error' };
      try {
        const response = await verifyPurlDob({ token, dobYear, dobMonth, dobDay });
        if (response.status === 'verified') {
          setVerifiedDob(`${dobYear}-${dobMonth}-${dobDay}`);
          trackEvent('purl-dob-verified', {});
          return { status: 'verified' };
        }
        if (response.status === 'mismatch') {
          trackEvent('purl-dob-mismatch', { attemptsRemaining: response.attemptsRemaining });
          return { status: 'mismatch', attemptsRemaining: response.attemptsRemaining };
        }
        // locked | invalid | already_enrolled | configuration_error — PURL can't
        // continue; fall through to normal onboarding. `locked` keeps its own
        // analytics + status; the rest share the generic `abandoned` terminal.
        if (response.status === 'locked') {
          trackEvent('purl-dob-locked', {});
          setStatus('locked');
        } else {
          setStatus('abandoned');
        }
        return { status: 'ended' };
      } catch {
        return { status: 'error' };
      }
    },
    [token]
  );

  // "Verify another way" (Sparta's escape link from the DOB mismatch state):
  // abandon PURL so the resolver falls the member through to normal onboarding.
  const verifyAnotherWay = useCallback(() => {
    trackEvent('purl-verify-another-way-clicked', {});
    setStatus('abandoned');
  }, []);

  // Create the account via sign-up. On `created` the api fn has already
  // stored the returned JWT tokens, so we just refreshUser() to flip authStatus
  // and let the resolver advance. Other statuses are normalized for the screen.
  const submitPurlAccount = useCallback(
    async (payload: PurlSignUpPayload): Promise<PurlSubmitResult> => {
      try {
        const response = await purlSignUp(payload);
        if (response.status === 'created') {
          await refreshUser();
          return { status: 'created' };
        }
        if (response.status === 'mismatch') {
          return { status: 'mismatch', attemptsRemaining: response.attemptsRemaining };
        }
        if (response.status === 'locked') return { status: 'locked' };
        if (response.status === 'already_enrolled') return { status: 'already_enrolled' };
        return { status: 'error' }; // configuration_error
      } catch {
        return { status: 'error' };
      }
    },
    [refreshUser]
  );

  // Kick validation once auth has settled to unauthenticated and the token + org
  // params are present (deep link / page refresh on an entry route). Gating on
  // `isUnauthenticated` is the "check for a 401 first" step — a signed-in user
  // never validates. Re-runs only if the token itself changes.
  useEffect(() => {
    if (!isUnauthenticated || !tokenParam || !orgParam) return;
    if (validatedTokenRef.current === tokenParam) return;
    validatedTokenRef.current = tokenParam;
    void runPurlValidate(tokenParam, orgParam);
  }, [isUnauthenticated, tokenParam, orgParam, runPurlValidate]);

  const purl = useMemo<PurlState>(
    () => ({ status, token, orgName, verifiedDob, redirectUrl }),
    [status, token, orgName, verifiedDob, redirectUrl]
  );

  return { purl, verifyDob, verifyAnotherWay, submitPurlAccount };
}
